/**
 * HxVideoGrid Component
 *
 * A dynamic multi-participant video grid that composes `hx-video-tile` instances. Automatically selects the optimal CSS
 * grid layout for 1–9+ participants, supports spotlight/gallery modes, a self-view PIP, and an overflow chip.
 *
 * Layout tiers (auto mode): 1 → full-screen solo 2 → 50/50 side-by-side 3 → 2 top + 1 bottom (full-width) 4 → 2×2 grid
 * 5 → 3+2 staggered 6 → 3×2 grid 7+ → 3×3 gallery with "+N more" overflow chip
 *
 * Spotlight mode: One participant (pinned or loudest speaker) takes 75% width; remaining participants fill a vertical
 * strip on the right.
 *
 * @example
 *   ```html
 *   <hx-video-grid
 *     [participants]="call.participants()"
 *     [pinnedParticipantId]="pinnedId()"
 *     (pinParticipant)="pin($event)"
 *   />
 *   ```;
 *
 * @package @complexcare/harmony
 */

import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { HxButton } from '../button/button';
import { CallParticipant } from '../call-participants/call-participants.types';
import { HxIcon } from '../icon/icon';
import { HxVideoTile } from '../video-tile/video-tile';
import { VideoGridLayout, VideoGridSelfViewPosition, VideoGridTier } from './video-grid.types';

/** Maximum tiles visible in gallery mode before overflow chip appears */
const GALLERY_MAX = 9;

/** Derive grid tier from visible participant count */
function getTier(count: number): VideoGridTier {
  if (count <= 1) return 'solo';
  if (count === 2) return 'duo';
  if (count === 3) return 'trio';
  if (count === 4) return 'quad';
  if (count === 5) return 'five';
  if (count === 6) return 'six';
  return 'many';
}

@Component({
  selector: 'hx-video-grid',
  standalone: true,
  imports: [HxVideoTile, HxIcon, HxButton],
  templateUrl: './video-grid.html',
  styleUrls: ['./video-grid.css'],
  host: {
    class: 'hx-video-grid',
    '[class]': 'hostClasses()',
    role: 'region',
    'aria-label': 'Video call participants',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxVideoGrid {
  // ── Inputs ────────────────────────────────────────────────────────────────

  /** Live list of participants – update the array reference to re-render */
  readonly participants = input<CallParticipant[]>([]);

  /** Layout mode. 'auto' selects the tier based on count. */
  readonly layout = input<VideoGridLayout>('auto');

  /** ID of the participant to spotlight/pin */
  readonly pinnedParticipantId = input<string | null>(null);

  /** Maximum tiles to show before the overflow chip (gallery only) */
  readonly maxVisible = input<number>(GALLERY_MAX);

  /** Show the local user's self-view as a corner PIP */
  readonly showSelfView = input<boolean>(true);

  /** Corner position of the self-view PIP */
  readonly selfViewPosition = input<VideoGridSelfViewPosition>('bottom-right');

  /** Show participant name labels on every tile */
  readonly showNames = input<boolean>(true);

  // ── Outputs ───────────────────────────────────────────────────────────────

  /** Emitted with the participant to pin/unpin when a tile is clicked */
  readonly pinParticipant = output<CallParticipant>();

  /** Emitted when the "+N more" chip is clicked */
  readonly showMoreClicked = output<void>();

  // ── Derived state ─────────────────────────────────────────────────────────

  /** Local (self-view) participant – rendered separately as PIP */
  protected readonly localParticipant = computed(() => this.participants().find((p) => p.isLocal) ?? null);

  /** Non-local participants for the main grid */
  private readonly remoteParticipants = computed(() => this.participants().filter((p) => !p.isLocal));

  /** Effective layout: resolve 'auto' to 'spotlight' or 'gallery' */
  private readonly effectiveLayout = computed<'spotlight' | 'gallery'>(() => {
    if (this.layout() !== 'auto') return this.layout() as 'spotlight' | 'gallery';
    // Auto: use spotlight when someone is pinned, gallery otherwise
    return this.pinnedParticipantId() ? 'spotlight' : 'gallery';
  });

  /** The spotlighted participant (pinned → loudest speaker → first) */
  protected readonly spotlightParticipant = computed<CallParticipant | null>(() => {
    if (this.effectiveLayout() !== 'spotlight') return null;
    const remotes = this.remoteParticipants();
    if (!remotes.length) return null;
    const pinned = remotes.find((p) => p.id === this.pinnedParticipantId());
    if (pinned) return pinned;
    return remotes.find((p) => p.isSpeaking) ?? remotes[0];
  });

  /** Participants shown in the sidebar strip (spotlight mode) */
  protected readonly sidebarParticipants = computed<CallParticipant[]>(() => {
    const spotlight = this.spotlightParticipant();
    if (!spotlight) return [];
    return this.remoteParticipants().filter((p) => p.id !== spotlight.id);
  });

  /** Participants shown in the gallery grid */
  protected readonly galleryParticipants = computed<CallParticipant[]>(() => {
    if (this.effectiveLayout() !== 'gallery') return [];
    return this.remoteParticipants().slice(0, this.maxVisible());
  });

  /** Number of participants hidden behind the overflow chip */
  protected readonly overflowCount = computed<number>(() => {
    if (this.effectiveLayout() !== 'gallery') return 0;
    return Math.max(0, this.remoteParticipants().length - this.maxVisible());
  });

  /** Grid tier for auto CSS class */
  protected readonly tier = computed<VideoGridTier>(() => getTier(this.galleryParticipants().length));

  protected readonly isSpotlight = computed(() => this.effectiveLayout() === 'spotlight');

  protected readonly hostClasses = computed(() => ({
    'hx-video-grid--spotlight': this.isSpotlight(),
    'hx-video-grid--gallery': !this.isSpotlight(),
    [`hx-video-grid--${this.tier()}`]: !this.isSpotlight(),
    [`hx-video-grid--self-${this.selfViewPosition()}`]: true,
  }));

  protected isPinned(p: CallParticipant): boolean {
    return p.id === this.pinnedParticipantId();
  }
}
